/**
 * PreviewVerificationLayer — internal preview page (Preview* pattern) mounting
 * the entire Phase 1 verification component layer with realistic Summit Ridge
 * fixture data. First real consumer of the layer + the owner's eyes-on surface.
 * Fixtures only — no live data, no auth hooks; safe under ProtectedRoute and in
 * the local render harness alike.
 */
import React, { useMemo, useState } from 'react';
import VerifiedValue, { ProvenanceKind } from '../components/shared/VerifiedValue';
import SourceDrawer, { Transaction } from '../components/shared/SourceDrawer';
import EngineAuditTable, { EngineRow } from '../components/shared/EngineAuditTable';
import AgentTrail, { GroundedNarrative, DeliberationEvent } from '../components/shared/AgentTrail';
import AttestationGate from '../components/shared/AttestationGate';

const PROVENANCES: ProvenanceKind[] = ['E', 'AI', 'doc', 'override'] as ProvenanceKind[];
const STATUSES = ['fresh', 'stale', 'unavailable', 'contested'] as const;

const FIXTURE_TXNS: Transaction[] = Array.from({ length: 120 }, (_, i) => ({
  id: `txn-${i + 1}`,
  date: `2023-${String((i % 12) + 1).padStart(2, '0')}-15`,
  vendor: ['Sysco Foods', 'TX Utilities Co', 'Summit Payroll', 'Hartford Premium', 'Linen Service'][i % 5],
  amount: Math.round((1200 + i * 37.5) * 100) / 100,
  description: ['Food inventory', 'Utilities', 'Payroll run', 'Insurance premium', 'Linen contract'][i % 5],
} as Transaction));

const ENGINE_IDS = [
  'merchant_reconciliation', 'merchant_revenue_augmentation', 'bottom_up', 'enhanced_claim',
  'dual_method', 'mitigation', 'impact', 'epi', 'policy_deductions', 'pv', 'fair_settlement',
  'case_strength', 'projection_trend', 'two_period', 'capacity_ceiling',
  'non_recurring_item_detector', 'catch_up_sales', 'monthly_limit_cap', 'extra_expense_cap', 'property',
];
const FIXTURE_ENGINES: EngineRow[] = ENGINE_IDS.slice(0, 17).map((id, i) => ({
  engine_id: id,
  output_value: id === 'bottom_up' ? 155309.37 : Math.round((9000 + i * 4321.9) * 100) / 100,
  method_name: id === 'two_period' ? 'seasonal_two_period' : 'trend_average_v2',
  version: '2.0.0',
  computed_at: '2026-07-24T18:40:00Z',
  freshness: i === 3 ? 'stale' : 'fresh',
  support_status: i === 5 ? 'CONTESTED' : 'SUPPORTED',
  verifier_verdict: i === 5 ? 'blocker' : 'pass',
  review_state: i < 14 ? 'approved' : 'unreviewed',
}));

const FIXTURE_EVENTS: DeliberationEvent[] = [
  { agent: 'ai:forensic', round: 1, tool_name: 'get_engine_outputs', inputs: { report_id: 'd6d88aea' }, result_summary: 'Loaded 17 canonical engine outputs', duration_ms: 412, ts: '2026-07-24T18:41:02Z' },
  { agent: 'ai:forensic', round: 1, tool_name: 'run_monte_carlo', inputs: { iterations: 10000 }, result_summary: 'P25 $148,220 / P50 $152,461 / P75 $158,974', duration_ms: 2210, ts: '2026-07-24T18:41:09Z' },
  { agent: 'ai:attorney', round: 2, tool_name: 'query_war_room_intelligence', inputs: { query: 'civil authority causation TX' }, result_summary: 'V-Test line applied; 3 authorities cited', duration_ms: 980, ts: '2026-07-24T18:42:20Z' },
  { agent: 'ai:adjuster', round: 2, tool_name: 'get_merchant_reconciliation', inputs: { year: 2022 }, result_summary: 'Explained 96.4% of platform revenue', duration_ms: 640, ts: '2026-07-24T18:42:41Z' },
];

const NARRATIVE =
  'The reconstructed net supported claim is $155,309.37 [CALC:bottom_up] after the waiting-period ' +
  'deduction of ($3,090.63) applies [CALC:policy_deductions]. An earlier draft asserted $84,200 in ' +
  'additional exposure that no engine has produced.';

export default function PreviewVerificationLayer() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [gateOpen, setGateOpen] = useState(false);
  const fetchTransactions = useMemo(
    () => async ({ offset, limit }: { offset: number; limit: number }) =>
      FIXTURE_TXNS.slice(offset, offset + limit),
    [],
  );

  const section: React.CSSProperties = { marginBottom: 28 };
  const h2: React.CSSProperties = {
    fontSize: 12, fontWeight: 600, letterSpacing: '.1em', textTransform: 'uppercase',
    color: 'var(--color-text-muted)', borderBottom: '1px solid var(--color-border)', paddingBottom: 8,
  };
  const card: React.CSSProperties = {
    background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)',
    borderRadius: 6, padding: 16, marginTop: 14,
  };

  return (
    <div style={{ background: 'var(--color-bg-base)', minHeight: '100vh', padding: 32, color: 'var(--color-text-primary)' }} className="font-sans">
      <h1 className="font-serif" style={{ fontSize: 26, fontWeight: 400, marginTop: 0 }}>
        Verification Layer — preview (Summit Ridge fixtures)
      </h1>

      <section style={section}>
        <h2 style={h2}>VerifiedValue — every provenance × status</h2>
        <div style={{ ...card, display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 16 }}>
          {PROVENANCES.map((p) =>
            STATUSES.map((s) => (
              <div key={`${p}-${s}`} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 10, color: 'var(--color-text-dim)' }} className="font-mono">{p} · {s}</span>
                <VerifiedValue
                  value={s === 'unavailable' ? null : 155309.37}
                  provenance={p}
                  status={s}
                  onDrill={p === 'E' && s === 'fresh' ? () => setDrawerOpen(true) : undefined}
                />
              </div>
            )),
          )}
        </div>
        <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 8 }}>
          The E·fresh figure is drillable — click it to open the SourceDrawer.
        </p>
      </section>

      <section style={section}>
        <h2 style={h2}>EngineAuditTable — 20 expected, 17 run, 3 NOT RUN</h2>
        <div style={card}>
          <EngineAuditTable engines={FIXTURE_ENGINES} expectedEngines={ENGINE_IDS} />
        </div>
      </section>

      <section style={section}>
        <h2 style={h2}>AgentTrail + GroundedNarrative</h2>
        <div style={card}>
          <GroundedNarrative text={NARRATIVE} />
        </div>
        <div style={card}>
          <AgentTrail events={FIXTURE_EVENTS} />
        </div>
      </section>

      <section style={section}>
        <h2 style={h2}>AttestationGate — document mode</h2>
        <div style={card}>
          <button
            type="button"
            onClick={() => setGateOpen(true)}
            style={{
              height: 32, padding: '0 16px', borderRadius: 6, border: 'none',
              background: 'var(--color-brand)', color: 'var(--color-on-brand)',
              fontSize: 12, fontWeight: 600, cursor: 'pointer',
            }}
          >
            Open attestation
          </button>
        </div>
      </section>

      <SourceDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        summary={[
          { label: 'Net supported claim', value: 155309.37 },
          { label: 'BI limit (policy)', value: 600000 },
        ]}
        engineRun={{
          method_name: 'trend_average_v2',
          version: '2.0.0',
          computed_at: '2026-07-24T18:40:00Z',
          freshness_status: 'fresh',
          inputs: { benchmark_months: 12, coverage_months: 6, naics: '722511' },
        }}
        fetchTransactions={fetchTransactions}
        sourceDocument={{ reportId: 'd6d88aea', sourceFile: 'summit-ridge-1065-2022.pdf', sourcePage: 1 }}
      />

      <AttestationGate
        open={gateOpen}
        onOpenChange={setGateOpen}
        checklist={[
          { label: 'Engines verified (17/20 run, 14 approved)', ok: true },
          { label: 'Blockers resolved', ok: true },
          { label: 'Extraction certified', ok: true },
        ]}
        forcedSurface={[
          {
            title: 'CP 04 11 endorsement grounding',
            body: 'The protective-safeguards verifier flagged this policy for endorsement review. Read the grounding warning against the policy document before attesting.',
          },
        ]}
        acknowledgments={[
          { id: 'trace-demand', label: 'I traced the net supported claim to its engine source (bottom_up)' },
          { id: 'cp0411', label: 'I reviewed the CP 04 11 endorsement-grounding warning against the policy' },
        ]}
        signerName="Tyler Reagan"
        digest={[
          { label: 'Net supported claim', value: '$155,309.37' },
          { label: 'Less waiting-period threshold', value: '($3,090.63)' },
          { label: 'BI limit (ceiling)', value: '$600,000.00' },
        ]}
        contentHash="9f2ac1d6e88b"
        onAttest={() => setGateOpen(false)}
      />
    </div>
  );
}
